import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';

/** Container chrome. `plain` sits directly on the page; `card` adds the bordered white panel. */
export type EmptyStateVariant = 'plain' | 'card';

/**
 * In-panel empty-list placeholder: illustration, title, description and an optional single
 * call-to-action. Replaces the per-feature `empty-campaign` / `empty-segment` / `empty-package`
 * components, which were re-implementations of the same layout.
 *
 * Purely presentational — no data access and no feature logic.
 *
 * Sibling component: `app-empty-result` is the *full-page* empty result (viewport-height
 * container, its own type scale, 23 consumers). This component is the in-panel/in-card variant;
 * the two are deliberately kept separate rather than merged behind a dozen override knobs.
 *
 * Container geometry is tunable from the consumer via CSS custom properties so each call site
 * keeps its exact appearance: `--empty-state-padding`, `--empty-state-min-height`,
 * `--empty-state-background`.
 *
 * @component
 * @selector app-empty-state
 * @example
 *   <app-empty-state
 *     image="assets/images/package/Illustrations.svg"
 *     imageAlt="No Campaigns"
 *     [title]="title"
 *     [description]="description"
 *     actionLabel="Create Campaign"
 *     [showAction]="true"
 *     (action)="onAdd()"
 *   ></app-empty-state>
 *
 * @input {string} image - Illustration source. The illustration is omitted when empty.
 * @input {string} imageAlt - Alt text for the illustration.
 * @input {string} title - Headline. Omitted when empty.
 * @input {string} description - Supporting copy. Omitted when empty.
 * @input {string} actionLabel - Text of the call-to-action button.
 * @input {boolean} showAction - Whether to render the call-to-action.
 * @input {string | null} actionIcon - Image source for the button icon. When null an inline plus
 *   glyph is drawn instead.
 * @input {EmptyStateVariant} variant - `plain` (default) or `card` for the bordered panel.
 * @input {string} testId - Root `data-testid`, and the stem for `<testId>-title`,
 *   `<testId>-description` and `<testId>-action-button`. Defaults to the generic `empty-state`;
 *   consumers pass a feature-scoped value (e.g. `campaigns-empty-state`).
 * @output {EventEmitter<void>} action - The user activated the call-to-action.
 *
 * Key method:
 * - onAction(): emits the `action` output.
 */
@Component({
  selector: 'app-empty-state',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './empty-state.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./empty-state.component.scss'],
})
export class EmptyStateComponent {
  @Input() image = '';
  @Input() imageAlt = '';
  @Input() title = '';
  @Input() description = '';
  @Input() actionLabel = '';
  @Input() showAction = false;
  @Input() actionIcon: string | null = null;
  @Input() variant: EmptyStateVariant = 'plain';
  @Input() testId = 'empty-state';

  @Output() action = new EventEmitter<void>();

  onAction(): void {
    this.action.emit();
  }
}
